import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to get the agent thread ID for a project
 * 
 * This provides a clean way to lookup the agent thread associated with a project
 * for opening the project chat interface.
 */
export function useProjectAgentThread(projectId: string) {
  return useQuery({
    queryKey: ['project-agent-thread', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      
      const { data, error } = await supabase
        .from('planner_goals_workbooks')
        .select('agent_thread_id')
        .eq('workbook_id', projectId)
        .single();
        
      if (error) {
        if (error.code === 'PGRST116') return null; // No rows found
        throw error;
      }
      
      return data.agent_thread_id;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes - agent thread IDs don't change often
  });
}

/**
 * Direct function to get project agent thread ID
 * Use this for one-off lookups outside of React components
 */
export async function getProjectAgentThreadId(projectId: string): Promise<string | null> {
  if (!projectId) return null;
  
  const { data, error } = await supabase
    .from('planner_goals_workbooks')
    .select('agent_thread_id')
    .eq('workbook_id', projectId)
    .single();
    
  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    throw error;
  }
  
  return data.agent_thread_id;
} 